
import { destroySafe } from '@deqo/foundation';
import { ObjectManager } from '../../../concepts/objects';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical } from '../../canvas/entities/Physical';
import { Burdens } from '../../canvas/extensibility/Burdens';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { ActiveObject, resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { FeatureManager } from '../feature/FeatureManager';
import { Hand } from './Hand';
import { HandControl } from './HandControl';
import { HandInfo } from './HandInfo';
import { HandLayout } from './HandLayout';
import { HoldLayout } from './HoldLayout';

export class HandMechanics extends ActiveObject {

    @resolve(Camera) private camera!: Camera;
    @resolve(FeatureManager) private features!: FeatureManager;
    @resolve(HandControl) private control!: HandControl;
    @resolve(HandLayout) private handLayout!: HandLayout;
    @resolve(HoldLayout) private holdLayout!: HoldLayout;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(EditorSession) private session!: EditorSession;

    private trash = new Burdens();

    public start(): void {
        this.burden(
            this.features.define('Hands', this.onFeatureStateChange),
            this.session.observe(MyRoleInfo, () => this.onFeatureStateChange(!!this.features.get('Hands'))),
            () => this.onFeatureStateChange(false),
        );
    }

    private onFeatureStateChange = (enabled: boolean) => {
        destroySafe(this.trash);
        this.trash = new Burdens();
        this.session.update(HandInfo, HandInfo.empty);
        if (!enabled || !this.session.get(MyRoleInfo).canParticipate) return;
        const hand = this.tryInitializeHand();
        if (!hand) return;
        this.updateLayout(hand);
        this.trash.add(
            this.camera.observe(() => this.updateLayout(hand)),
            this.control.observe(() => this.updateLayout(hand)),
            this.session.observe(AdapterInfo, () => this.updateLayout(hand)),
            this.objects.on('component', 'affected', ([cmpt]) => {
                if (cmpt === hand) this.updateLayout(hand);
            }),
        );
    };

    private tryInitializeHand() {
        try {
            const hand = this.getOrAllocateHandObject();
            this.session.update(HandInfo, {
                enabled: true,
                object: hand.id,
            });
            return hand;
        }
        catch {
            // If usage exceeded or readonly, this can fail
            return null;
        }
    }

    private getOrAllocateHandObject() {
        const found = Array.from(this.objects.getAll(Hand)).find(x => x.userId === this.me.id);
        if (!!found) return found;
        return this.objects.update(u => {
            const hand = u.create(Hand);
            hand.userId = this.me.id;
            return hand;
        });
    }

    private updateLayout(hand: Hand) {

        const frame = Rect.fromSize(this.camera.viewport);
        if (!frame.equals(this.handLayout.frame)) {
            this.handLayout.update(frame);
        }

        const items = hand?.alive ? hand.polarize(Physical) : [];

        // const ai = this.session.get(AdapterInfo);
        // if (ai.target !== 'none') {
        //     const overrideTarget = hold.find(x => x.id === ai.target);
        //     if (!!overrideTarget) {
        //         inputs.target = overrideTarget.as(Physical);
        //     }
        // }

        this.holdLayout.update({
            ...this.control.getState(),
            ...this.handLayout.getState(),
            items,
        });
    }
}
